import type { AuthRepositoryInterface, AuthResult } from '../../domain/interfaces/AuthRepositoryInterface';
import { UserEntity } from '../../domain/entities/User';
import { httpService } from '@/services/HttpService';
import { redirectToAuthService } from '@/services/AuthServiceNavigation';
import type {
  LoginData,
  RegisterData,
  ForgotPasswordData,
  ResetPasswordData,
  User,
  OAuthRedirectResponse,
  OAuthCallbackResponse
} from '@/types';

export class HttpAuthRepository implements AuthRepositoryInterface {
  async login(_credentials: LoginData): Promise<AuthResult> {
    redirectToAuthService('/login');

    return Promise.reject(new Error('redirecting_to_auth_service'));
  }

  async register(_data: RegisterData): Promise<UserEntity> {
    redirectToAuthService('/register');

    return Promise.reject(new Error('redirecting_to_auth_service'));
  }

  async logout(authServiceAccessToken?: string): Promise<void> {
    if (authServiceAccessToken) {
      await this.oauthLogout(authServiceAccessToken);
      return;
    }

    // Fallback if no token (should not happen in normal flow)
    window.location.href = '/';
  }

  async forgotPassword(_data: ForgotPasswordData): Promise<{ message: string }> {
    redirectToAuthService('/forgot-password');

    return Promise.reject(new Error('redirecting_to_auth_service'));
  }

  async resetPassword(data: ResetPasswordData): Promise<{ message: string }> {
    redirectToAuthService(`/reset-password/${encodeURIComponent(data.token)}`, {
      email: data.email,
    });

    return Promise.reject(new Error('redirecting_to_auth_service'));
  }

  async getCurrentUser(): Promise<UserEntity> {
    const response = await httpService.get<{ data: User }>('/auth/me');
    return UserEntity.fromApi(response.data);
  }

  async getOAuthRedirectUrl(flow?: 'silent'): Promise<OAuthRedirectResponse> {
    return await httpService.public.get<OAuthRedirectResponse>('/auth/oauth/redirect', flow ? { flow } : undefined);
  }

  async oauthCallback(code: string, state: string): Promise<OAuthCallbackResponse> {
    return await httpService.public.get<OAuthCallbackResponse>('/auth/oauth/callback', { code, state });
  }

  async oauthLogout(authServiceAccessToken: string): Promise<void> {
    const response = await fetch(`${import.meta.env.VITE_AUTH_BASE || 'http://localhost:8002'}/api/oauth/logout`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': `Bearer ${authServiceAccessToken}`,
      },
    });

    if (!response.ok) {
      console.error('Failed to logout from auth service');
    }
  }
}
